import React, { useState, useMemo, useEffect } from "react";
import BarChart from "../../charts/BarChart";
import {
  Row,
  Col,
  Divider,
  Select,
  Space,
  Table,
  Button,
  Switch,
  Radio,
  Spin,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getPlanesCompra } from "../../../services/financiero/plandecompras/planesdecompra";

/**
 * Componente de informe visual para los planes de compra.
 * Recibe la data de planes como prop y muestra varios gráficos y una tabla profesional.
 * @param {Array} planes - Array de objetos de planes de compra
 */
const InformePlanesCompras = () => {
  // Filtros locales
  const [departamentoFiltro, setDepartamentoFiltro] = useState([]);
  const [municipioFiltro, setMunicipioFiltro] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState([]);
  const [planes, setPlanes] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Función para cargar los datos desde el servicio
  const fetchPlanes = async () => {
    try {
      const response = await getPlanesCompra(); // Llama al servicio
      if (response.status === 200) {
        const processedData = response.data.map((item) => ({
          ...item,
          dataItems:
            typeof item.dataItems === "string"
              ? JSON.parse(item.dataItems)
              : item.dataItems,
          conceptoInversion:
            typeof item.conceptoInversion === "string"
              ? JSON.parse(item.conceptoInversion)
              : item.conceptoInversion,
        }));

        setPlanes(processedData);
      } else {
        console.error(
          "Error al obtener los planes de compra:",
          response.message
        );
      }
    } catch (error) {
      console.error("❌ Error al cargar los planes de compra:", error);
    }
  };

  useEffect(() => {
    fetchPlanes();
  }, []);

  // Obtener departamentos únicos
  const departamentosUnicos = useMemo(() => {
    return [...new Set(planes.map((p) => p.departamento).filter(Boolean))];
  }, [planes]);

  // Obtener municipios únicos según los departamentos seleccionados
  const municipiosUnicos = useMemo(() => {
    if (!departamentoFiltro || departamentoFiltro.length === 0) return [];
    return [
      ...new Set(
        planes
          .filter((p) => departamentoFiltro.includes(p.departamento))
          .map((p) => p.municipio)
          .filter(Boolean)
      ),
    ];
  }, [planes, departamentoFiltro]);

  // Obtener categorías únicas
  const categoriasUnicas = useMemo(() => {
    const setCategorias = new Set();
    planes.forEach((plan) => {
      plan.dataItems?.forEach((section) => {
        section.items?.forEach((item) => {
          if (item.nombreCategoria) setCategorias.add(item.nombreCategoria);
        });
      });
    });
    return [...setCategorias];
  }, [planes]);

  // Filtrar los planes según los filtros seleccionados
  const planesFiltrados = useMemo(() => {
    return planes.filter((plan) => {
      if (
        departamentoFiltro.length > 0 &&
        !departamentoFiltro.includes(plan.departamento)
      )
        return false;
      if (
        municipioFiltro.length > 0 &&
        !municipioFiltro.includes(plan.municipio)
      )
        return false;
      if (categoriaFiltro.length > 0) {
        // Al menos un item del plan debe tener la categoría seleccionada
        const tieneCategoria = plan.dataItems?.some((section) =>
          section.items?.some((item) =>
            categoriaFiltro.includes(item.nombreCategoria)
          )
        );
        if (!tieneCategoria) return false;
      }
      return true;
    });
  }, [planes, departamentoFiltro, municipioFiltro, categoriaFiltro]);

  // 1. Total de proyectos por departamento (valor total y cantidad de proyectos)
  const departamentos = {};
  const cantidadProyectosPorDepto = {};
  planesFiltrados.forEach((plan) => {
    const dep = plan.departamento || "Sin departamento";
    const total =
      plan.conceptoInversion?.find((c) => c.concepto === "TOTAL PROYECTO")
        ?.valor || 0;
    departamentos[dep] = (departamentos[dep] || 0) + total;
    cantidadProyectosPorDepto[dep] = (cantidadProyectosPorDepto[dep] || 0) + 1;
  });
  const labelsDepartamentos = Object.keys(departamentos);
  const dataDepartamentos = Object.values(departamentos);
  const dataCantidadProyectos = labelsDepartamentos.map(
    (dep) => cantidadProyectosPorDepto[dep] || 0
  );

  // 2. Distribución de conceptos de inversión (acumulado)
  const conceptos = {};
  const cantidadPlanesPorConcepto = {};
  planesFiltrados.forEach((plan) => {
    const conceptosEnPlan = new Set();
    plan.conceptoInversion?.forEach((c) => {
      if (c.concepto !== "TOTAL PROYECTO") {
        conceptos[c.concepto] = (conceptos[c.concepto] || 0) + c.valor;
        conceptosEnPlan.add(c.concepto);
      }
    });
    conceptosEnPlan.forEach((concepto) => {
      cantidadPlanesPorConcepto[concepto] =
        (cantidadPlanesPorConcepto[concepto] || 0) + 1;
    });
  });
  const labelsConceptos = Object.keys(conceptos);
  const dataConceptos = Object.values(conceptos);
  const dataCantidadPlanesPorConcepto = labelsConceptos.map(
    (concepto) => cantidadPlanesPorConcepto[concepto] || 0
  );

  // 3. Cantidad de productos y valor total por categoría
  const categorias = {};
  const valorPorCategoria = {};
  planesFiltrados.forEach((plan) => {
    plan.dataItems?.forEach((section) => {
      section.items?.forEach((item) => {
        const cat = item.nombreCategoria || "Sin categoría";
        if (categoriaFiltro.length === 0 || categoriaFiltro.includes(cat)) {
          categorias[cat] = (categorias[cat] || 0) + 1;
          valorPorCategoria[cat] =
            (valorPorCategoria[cat] || 0) + (item.valorTotal || 0);
        }
      });
    });
  });
  const labelsCategorias = Object.keys(categorias);
  const dataCategorias = labelsCategorias.map((cat) => categorias[cat]);
  const dataValorCategorias = labelsCategorias.map(
    (cat) => valorPorCategoria[cat] || 0
  );

  // 4. Valor total y cantidad total por tipo de producto (filtrado por categoría)
  const tiposProducto = {};
  const cantidadProductosPorTipo = {};
  planesFiltrados.forEach((plan) => {
    plan.dataItems?.forEach((section) => {
      section.items?.forEach((item) => {
        const cat = item.nombreCategoria || "Sin categoría";
        if (categoriaFiltro.length === 0 || categoriaFiltro.includes(cat)) {
          const tipo = item.nombreTipoProducto || "Sin tipo";
          tiposProducto[tipo] =
            (tiposProducto[tipo] || 0) + (item.valorTotal || 0);
          cantidadProductosPorTipo[tipo] =
            (cantidadProductosPorTipo[tipo] || 0) + (item.cantidad || 0);
        }
      });
    });
  });
  const labelsTipos = Object.keys(tiposProducto);
  const dataTipos = labelsTipos.map((tipo) => tiposProducto[tipo]);
  const dataCantidadProductosPorTipo = labelsTipos.map(
    (tipo) => cantidadProductosPorTipo[tipo] || 0
  );

  // 5. Proyectos por estado de aceptación
  const estadosAceptacion = {
    "No registrado": 0,
    Diligenciado: 0,
    Completado: 0,
  };
  planesFiltrados.forEach((plan) => {
    if (plan.tiene_aceptacion === 0) {
      estadosAceptacion["No registrado"]++;
    } else if (plan.estado_aceptacion === 0) {
      estadosAceptacion["Diligenciado"]++;
    } else if (plan.estado_aceptacion === 1) {
      estadosAceptacion["Completado"]++;
    }
  });
  const labelsEstados = Object.keys(estadosAceptacion);
  const dataEstados = Object.values(estadosAceptacion);

  // 6. Proyectos por estado de delegación
  const estadosDelegacion = {
    Diligenciado: 0,
    Completado: 0,
  };
  planesFiltrados.forEach((plan) => {
    if (plan.estado_delegacion === 0) {
      estadosDelegacion["Diligenciado"]++;
    } else if (plan.estado_delegacion === 1) {
      estadosDelegacion["Completado"]++;
    }
  });
  const labelsDelegacion = Object.keys(estadosDelegacion);
  const dataDelegacion = Object.values(estadosDelegacion);

  // === TABLA PROFESIONAL: Productos por departamento y sus cantidades ===
  const productosSet = new Set();
  const departamentosSet = new Set();

  planesFiltrados.forEach((plan) => {
    const dep = plan.departamento || "Sin departamento";
    departamentosSet.add(dep);
    plan.dataItems?.forEach((section) => {
      section.items?.forEach((item) => {
        const prodKey = `${item.nombre_producto || "Sin nombre"}`;

        productosSet.add(prodKey);
      });
    });
  });

  // Estado para la agrupación de la tabla
  const [agrupacionTabla, setAgrupacionTabla] = useState("producto"); // "producto", "categoria", "concepto"

  // Construir la data para la tabla según la agrupación seleccionada
  let productos = [];
  let departamentosArray = Array.from(departamentosSet);
  let dataSource = [];
  let columnsTabla = [];

  if (agrupacionTabla === "producto") {
    // Agrupación por producto (nombre - código)
    productos = Array.from(productosSet);
    dataSource = productos.map((prodKey) => {
      let totalCantidad = 0;
      let totalValorUnitario = 0;
      let conteoValorUnitario = 0;
      const row = { key: prodKey, producto: prodKey, total: 0 };
      departamentosArray.forEach((dep) => {
        row[dep] = 0;
      });
      // Normalizar prodKey para comparación robusta
      const clave = (prodKey || "").trim().toLowerCase();
      planesFiltrados.forEach((plan) => {
        const dep = plan.departamento || "Sin departamento";
        plan.dataItems?.forEach((section) => {
          section.items?.forEach((item) => {
            const nombreProducto = (item.nombre_producto || "")
              .trim()
              .toLowerCase();
            const producto = (item.producto || "").trim().toLowerCase();
            // Evaluar si prodKey coincide con nombre_producto o producto
            if (
              clave === nombreProducto ||
              clave === producto ||
              (!nombreProducto && !producto && clave === "sin nombre")
            ) {
              const cantidad = item.cantidad || 0;
              row[dep] += cantidad;
              row.total += cantidad;
              totalCantidad += cantidad;
              if (
                item.precio_unitarioPesos &&
                !isNaN(Number(item.precio_unitarioPesos))
              ) {
                totalValorUnitario += Number(item.precio_unitarioPesos);
                conteoValorUnitario++;
              }
              row.codigo = item.codigo || "Sin código";
            }
          });
        });
      });
      const precioUnitarioPromedio =
        conteoValorUnitario > 0 ? totalValorUnitario / conteoValorUnitario : 0;
      row.precioUnitario = precioUnitarioPromedio;
      row.valorTotalPesos = precioUnitarioPromedio * totalCantidad;

      return row;
    });
    // Filtrar filas vacías: producto 'Sin nombre' y código 'Sin código' y sin datos
    dataSource = dataSource.filter((row) => {
      const sinNombre = !row.producto || row.producto === "Sin nombre";
      const sinCodigo = !row.codigo || row.codigo === "Sin código";
      const sinDatos =
        (!row.total || row.total === 0) &&
        departamentosArray.every((dep) => !row[dep] || row[dep] === 0);
      return !(sinNombre && sinCodigo && sinDatos);
    });
    columnsTabla = [
      {
        title: "Código",
        dataIndex: "codigo",
        key: "codigo",
        fixed: "left",
        width: 250,
        sorter: (a, b) => a.producto.localeCompare(b.codigo),
      },
      {
        title: "Producto (Nombre - Código)",
        dataIndex: "producto",
        key: "producto",
        fixed: "left",
        width: 300,
        sorter: (a, b) => a.producto.localeCompare(b.producto),
      },
      {
        title: "Precio Unitario (COP)",
        dataIndex: "precioUnitario",
        key: "precioUnitario",
        align: "right",
        width: 180,
        render: (value) =>
          value
            ? value.toLocaleString("es-CO", {
                style: "currency",
                currency: "COP",
              })
            : "-",
        sorter: (a, b) => a.precioUnitario - b.precioUnitario,
      },
      ...departamentosArray.map((dep) => ({
        title: dep,
        dataIndex: dep,
        key: dep,
        align: "center",
        width: 120,
        sorter: (a, b) => a[dep] - b[dep],
      })),
      {
        title: "Total Unidades",
        dataIndex: "total",
        key: "total",
        align: "center",
        width: 150,
        sorter: (a, b) => a.total - b.total,
        defaultSortOrder: "descend",
      },
      {
        title: "Valor Total (COP)",
        dataIndex: "valorTotalPesos",
        key: "valorTotalPesos",
        align: "right",
        width: 180,
        render: (value) =>
          value
            ? value.toLocaleString("es-CO", {
                style: "currency",
                currency: "COP",
              })
            : "-",
        sorter: (a, b) => a.valorTotalPesos - b.valorTotalPesos,
      },
    ];
  } else if (agrupacionTabla === "categoria") {
    // Agrupación por categoría (usando item.nombreCategoria real)
    const categoriasSet = new Set();
    planesFiltrados.forEach((plan) => {
      plan.dataItems?.forEach((section) => {
        section.items?.forEach((item) => {
          categoriasSet.add(item.nombreCategoria || "Sin categoría");
        });
      });
    });
    productos = Array.from(categoriasSet);
    dataSource = productos.map((cat) => {
      let totalCantidad = 0;
      let totalValor = 0;
      const row = { key: cat, categoria: cat, total: 0 };
      departamentosArray.forEach((dep) => {
        row[dep] = 0;
      });
      planesFiltrados.forEach((plan) => {
        const dep = plan.departamento || "Sin departamento";
        plan.dataItems?.forEach((section) => {
          section.items?.forEach((item) => {
            if ((item.nombreCategoria || "Sin categoría") === cat) {
              const cantidad = item.cantidad || 0;
              row[dep] += cantidad;
              row.total += cantidad;
              totalCantidad += cantidad;
              totalValor += item.valorTotal || 0;
            }
          });
        });
      });
      row.valorTotalPesos = totalValor;
      return row;
    });
    columnsTabla = [
      {
        title: "Categoría",
        dataIndex: "categoria",
        key: "categoria",
        fixed: "left",
        width: 200,
        sorter: (a, b) => a.categoria.localeCompare(b.categoria),
      },
      ...departamentosArray.map((dep) => ({
        title: dep,
        dataIndex: dep,
        key: dep,
        align: "center",
        width: 120,
        sorter: (a, b) => a[dep] - b[dep],
      })),
      {
        title: "Total Unidades",
        dataIndex: "total",
        key: "total",
        align: "center",
        width: 150,
        sorter: (a, b) => a.total - b.total,
        defaultSortOrder: "descend",
      },
      {
        title: "Valor Total (COP)",
        dataIndex: "valorTotalPesos",
        key: "valorTotalPesos",
        align: "right",
        width: 180,
        render: (value) =>
          value
            ? value.toLocaleString("es-CO", {
                style: "currency",
                currency: "COP",
              })
            : "-",
        sorter: (a, b) => a.valorTotalPesos - b.valorTotalPesos,
      },
    ];
  } else if (agrupacionTabla === "concepto") {
    // Agrupación por concepto de inversión (usando plan.conceptoInversion)
    const conceptosSet = new Set();
    planesFiltrados.forEach((plan) => {
      plan.conceptoInversion?.forEach((c) => {
        if (c.concepto !== "TOTAL PROYECTO") conceptosSet.add(c.concepto);
      });
    });
    productos = Array.from(conceptosSet);
    dataSource = productos.map((concepto) => {
      let totalCantidad = 0;
      let totalValor = 0;
      const row = { key: concepto, concepto, total: 0 };
      departamentosArray.forEach((dep) => {
        row[dep] = 0;
      });
      planesFiltrados.forEach((plan) => {
        const dep = plan.departamento || "Sin departamento";
        // Sumar el valor del concepto para el departamento
        const conceptoObj = plan.conceptoInversion?.find(
          (c) => c.concepto === concepto
        );
        if (conceptoObj) {
          row[dep] += 1; // Cuenta el plan como 1 para ese concepto en ese departamento
          row.total += 1;
          totalCantidad += 1;
          totalValor += conceptoObj.valor || 0;
        }
      });
      row.valorTotalPesos = totalValor;
      return row;
    });
    columnsTabla = [
      {
        title: "Concepto de Inversión",
        dataIndex: "concepto",
        key: "concepto",
        fixed: "left",
        width: 220,
        sorter: (a, b) => a.concepto.localeCompare(b.concepto),
      },
      ...departamentosArray.map((dep) => ({
        title: dep,
        dataIndex: dep,
        key: dep,
        align: "center",
        width: 120,
        sorter: (a, b) => a[dep] - b[dep],
      })),
      {
        title: "Total Planes",
        dataIndex: "total",
        key: "total",
        align: "center",
        width: 150,
        sorter: (a, b) => a.total - b.total,
        defaultSortOrder: "descend",
      },
      {
        title: "Valor Total (COP)",
        dataIndex: "valorTotalPesos",
        key: "valorTotalPesos",
        align: "right",
        width: 180,
        render: (value) =>
          value
            ? value.toLocaleString("es-CO", {
                style: "currency",
                currency: "COP",
              })
            : "-",
        sorter: (a, b) => a.valorTotalPesos - b.valorTotalPesos,
      },
    ];
  }

  // Función para exportar la tabla a Excel según la agrupación
  const exportarExcel = () => {
    let columnsExport = [];
    if (agrupacionTabla === "producto") {
      columnsExport = [
        { title: "Código", dataIndex: "codigo" },
        { title: "Nombre del producto", dataIndex: "producto" },
        { title: "Precio Unitario (COP)", dataIndex: "precioUnitario" },
        ...departamentosArray.map((dep) => ({ title: dep, dataIndex: dep })),
        { title: "Total Unidades", dataIndex: "total" },
        { title: "Valor Total (COP)", dataIndex: "valorTotalPesos" },
      ];
    } else if (agrupacionTabla === "categoria") {
      columnsExport = [
        { title: "Categoría", dataIndex: "categoria" },
        ...departamentosArray.map((dep) => ({ title: dep, dataIndex: dep })),
        { title: "Total Unidades", dataIndex: "total" },
        { title: "Valor Total (COP)", dataIndex: "valorTotalPesos" },
      ];
    } else if (agrupacionTabla === "concepto") {
      columnsExport = [
        { title: "Concepto de Inversión", dataIndex: "concepto" },
        ...departamentosArray.map((dep) => ({ title: dep, dataIndex: dep })),
        { title: "Total Planes", dataIndex: "total" },
        { title: "Valor Total (COP)", dataIndex: "valorTotalPesos" },
      ];
    }
    const ws = XLSX.utils.json_to_sheet(
      dataSource.map((row) => {
        const obj = {};
        columnsExport.forEach((col) => {
          obj[col.title] = row[col.dataIndex];
        });
        return obj;
      })
    );
    const wb = XLSX.utils.book_new();
    let sheetName =
      agrupacionTabla.charAt(0).toUpperCase() + agrupacionTabla.slice(1);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `tabla_${agrupacionTabla}.xlsx`);
  };

  // === GRÁFICA: Cantidad de planes de compra y valor total por departamento ===
  // Eje X: Departamento, Barras: cantidad de planes, valor total (COP)
  const resumenPorDepto = {};
  planesFiltrados.forEach((plan) => {
    const dep = plan.departamento || "Sin departamento";
    if (!resumenPorDepto[dep]) resumenPorDepto[dep] = { cantidad: 0, valor: 0 };
    resumenPorDepto[dep].cantidad++;
    const total =
      plan.conceptoInversion?.find((c) => c.concepto === "TOTAL PROYECTO")
        ?.valor || 0;
    resumenPorDepto[dep].valor += total;
  });
  const labelsResumenDepto = Object.keys(resumenPorDepto);
  const dataCantidadPlanes = labelsResumenDepto.map(
    (dep) => resumenPorDepto[dep].cantidad
  );
  const dataValorTotal = labelsResumenDepto.map(
    (dep) => resumenPorDepto[dep].valor
  );
  const chartDataPlanesValor = {
    labels: labelsResumenDepto,
    datasets: [
      {
        label: "Cantidad de Planes",
        data: dataCantidadPlanes,
        backgroundColor: "#0B80C4",
        yAxisID: "y",
      },
      {
        label: "Valor Total (COP)",
        data: dataValorTotal,
        backgroundColor: "#FFC107",
        yAxisID: "y1",
      },
    ],
  };

  // Función para abreviar valores monetarios
  function abreviarNumero(valor) {
    if (valor >= 1e9) return (valor / 1e9).toFixed(1) + "B";
    if (valor >= 1e6) return (valor / 1e6).toFixed(1) + "M";
    if (valor >= 1e3) return (valor / 1e3).toFixed(1) + "K";
    return valor.toLocaleString("es-CO");
  }

  // Datos para ChartCard (Chart.js)
  const chartDataConceptos = {
    labels: labelsConceptos,
    datasets: [
      {
        label: "Valor por Concepto",
        data: dataConceptos,
        backgroundColor: [
          "#0B80C4",
          "#4CAF50",
          "#FFC107",
          "#FF5722",
          "#9C27B0",
          "#607D8B",
        ],
      },
    ],
  };

  const chartDataTipos = {
    labels: labelsTipos,
    datasets: [
      {
        label: "Valor por Tipo de Producto",
        data: dataTipos,
        backgroundColor: [
          "#0B80C4",
          "#4CAF50",
          "#FFC107",
          "#FF5722",
          "#9C27B0",
          "#607D8B",
        ],
      },
    ],
  };

  if (!planes || planes.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
        <p style={{ marginTop: 16, fontSize: 16, color: "#888" }}>
          Cargando datos...
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ textAlign: "center", color: "#0B3360", fontWeight: "bold" }}>
        Informe de Planes de Compra
      </h2>
      {/* Filtros de departamento, municipio y tipo de producto */}
      {/* Reemplazo de Space por Row y Col para filtros responsivos */}
      <Row
        gutter={[12, 12]}
        style={{
          marginBottom: 24,
          width: "100%",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <Select
            allowClear
            mode="multiple"
            placeholder="Filtrar por Departamento"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children" // busca dentro del texto de las opciones
            value={departamentoFiltro}
            onChange={(value) => {
              setDepartamentoFiltro(value);
              setMunicipioFiltro([]);
            }}
            options={departamentosUnicos.map((dep) => ({
              value: dep,
              label: dep,
            }))}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <Select
            allowClear
            mode="multiple"
            placeholder="Filtrar por Municipio"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children" // busca dentro del texto de las opciones
            value={municipioFiltro}
            onChange={setMunicipioFiltro}
            disabled={departamentoFiltro.length === 0}
            options={municipiosUnicos.map((mun) => ({
              value: mun,
              label: mun,
            }))}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <Select
            allowClear
            mode="multiple"
            placeholder="Filtrar por Categoría"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children" // busca dentro del texto de las opciones
            value={categoriaFiltro}
            onChange={setCategoriaFiltro}
            options={categoriasUnicas.map((cat) => ({
              value: cat,
              label: cat,
            }))}
          />
        </Col>
      </Row>

      <Divider />
      <BarChart
        labels={labelsResumenDepto}
        data={dataValorTotal}
        totalRecords={dataCantidadPlanes}
        title="Cantidad de Planes de Compra y Valor Total (COP) por Departamento"
        xAxisTitle="Departamento"
        yAxisTitle="Valor Total (COP)"
        valueFormatter={abreviarNumero}
        bar1Label="Valor Total (COP)"
        bar2Label="Cantidad de Planes"
      />
      <Divider />
      <Row gutter={[32, 32]}>
        <Col xs={24} md={24}>
          <BarChart
            labels={labelsTipos}
            data={dataTipos}
            totalRecords={dataCantidadProductosPorTipo}
            title="Valor Total y Cantidad de Productos por Tipo de Producto"
            xAxisTitle="Tipo de Producto"
            yAxisTitle="Valor (COP)"
            valueFormatter={abreviarNumero}
            bar1Label="Valor Total (COP)"
            bar2Label="Cantidad de Productos"
          />
        </Col>
      </Row>
      <Divider />
      <Row gutter={[32, 32]}>
        <Col xs={24} md={12}>
          <BarChart
            labels={labelsConceptos}
            data={dataConceptos}
            totalRecords={dataCantidadPlanesPorConcepto}
            title="Distribución de Conceptos de Inversión"
            xAxisTitle="Concepto"
            yAxisTitle="Valor (COP)"
            valueFormatter={abreviarNumero}
            bar1Label="Valor (COP)"
            bar2Label="Cantidad de Planes"
          />
        </Col>
        <Col xs={24} md={12}>
          <BarChart
            labels={labelsCategorias}
            data={dataValorCategorias} // <-- Ahora la barra principal es Valor Total (COP)
            totalRecords={dataCategorias} // <-- Cantidad de Productos solo para tooltip y tabla
            title="Valor Total y Cantidad de Productos por Categoría"
            xAxisTitle="Categoría"
            yAxisTitle="Valor Total (COP)"
            valueFormatter={abreviarNumero}
            bar1Label="Valor Total (COP)"
            bar2Label="Cantidad de Productos"
          />
        </Col>
      </Row>
      <Divider />
      <Row gutter={[32, 32]}>
        <Col xs={24} md={12}>
          <BarChart
            labels={labelsEstados}
            data={dataEstados}
            totalRecords={[]}
            title="Planes de compra por estado de aceptación"
            xAxisTitle="Estado de Aceptación"
            yAxisTitle="Cantidad registros"
            bar1Label="Cantidad de registros"
            bar2Label=""
          />
        </Col>
        <Col xs={24} md={12}>
          <BarChart
            labels={labelsDelegacion}
            data={dataDelegacion}
            totalRecords={[]}
            title="Planes de compra por estado de delegación"
            xAxisTitle="Estado de Delegación"
            yAxisTitle="Cantidad registros"
            bar1Label="Cantidad registros"
            bar2Label=""
          />
        </Col>
      </Row>
      <Divider />
      <h3 style={{ margin: "24px 0 8px 0", color: "#0B3360" }}>
        Consolidado detallado por departamento
      </h3>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Radio.Group
          value={agrupacionTabla}
          onChange={(e) => setAgrupacionTabla(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="producto">Por Producto</Radio.Button>
          <Radio.Button value="categoria">Por Categoría</Radio.Button>
          <Radio.Button value="concepto">
            Por Concepto de Inversión
          </Radio.Button>
        </Radio.Group>
        <Button
          icon={<DownloadOutlined />}
          type="primary"
          onClick={exportarExcel}
        >
          Descargar Excel
        </Button>
      </div>
      <Table
        columns={columnsTabla}
        dataSource={dataSource}
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: dataSource.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        bordered
        size="middle"
      />
    </div>
  );
};

export default InformePlanesCompras;
